import { useEffect, useState } from 'react';

/**
 * Mock premium-entitlement store. Pure client-side, no backend.
 * Used so the team can test locked/unlocked content paths without a
 * real billing flow. Replace with the real entitlement source later.
 *
 * Storage shape:
 *   { active: boolean, since: string | null, mock: true }
 */

const STORAGE_KEY = 'explore-premium-mock-v1';

/**
 * When false (production default), all guided content is treated as accessible
 * and mock premium UI is hidden. When true (dev or VITE_FEATURE_PREMIUM_MOCK),
 * first-path-only gating + test unlock apply.
 */
export function isPremiumMockEnabled() {
  if (import.meta.env.DEV) return true;
  return import.meta.env.VITE_FEATURE_PREMIUM_MOCK === 'true';
}

/** @typedef {{ active: boolean, since: string | null, mock: true }} PremiumState */

const listeners = new Set();

function emit() {
  listeners.forEach((fn) => fn());
}

/** @returns {PremiumState} */
function defaultState() {
  return { active: false, since: null, mock: true };
}

function normalize(raw) {
  const d = defaultState();
  if (!raw || typeof raw !== 'object') return d;
  return {
    active: !!raw.active,
    since: typeof raw.since === 'string' ? raw.since : raw.active ? new Date().toISOString() : null,
    mock: true,
  };
}

/** @returns {PremiumState} */
export function loadPremium() {
  if (typeof window === 'undefined') return defaultState();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    return normalize(JSON.parse(raw));
  } catch {
    return defaultState();
  }
}

function persist(next) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // quota / private mode — ignore
  }
  emit();
}

/** @returns {boolean} */
export function hasPremium() {
  return loadPremium().active;
}

/** @param {boolean} active */
export function setPremium(active) {
  const next = active
    ? { active: true, since: new Date().toISOString(), mock: true }
    : { active: false, since: null, mock: true };
  persist(next);
  return next;
}

export function togglePremium() {
  return setPremium(!hasPremium());
}

/**
 * Subscribe to premium changes. Returns an unsubscribe fn.
 * @param {() => void} cb
 */
export function subscribePremium(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * Live-updating premium snapshot.
 * @returns {[boolean, (next: boolean) => void, PremiumState]}
 */
export function usePremium() {
  const [state, setState] = useState(() => loadPremium());
  useEffect(() => subscribePremium(() => setState(loadPremium())), []);
  return [state.active, setPremium, state];
}
